import { useState } from "react";
import { motion } from "framer-motion";
import { Users, TrendingUp, IndianRupee, Briefcase, Plus, Search, FileUp, Bell as BellIcon } from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar
} from "recharts";
import { advisorClients, commissionTrend } from "../lib/data";
import { DashHeader } from "./ClientDashboard";

const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function AdvisorDashboard() {
  const [search, setSearch] = useState("");
  const filtered = advisorClients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.id.toLowerCase().includes(search.toLowerCase())
  );
  const totalAUM = advisorClients.reduce((s, c) => s + c.aum, 0);
  const totalSIPs = advisorClients.reduce((s, c) => s + c.sips, 0);
  const commission = commissionTrend[commissionTrend.length - 1].value;

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="advisor-dashboard">
      <DashHeader title="Advisor Dashboard" subtitle="Welcome, Priya Mehta · ARN-162408" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard Icon={Users} label="Active Clients" value={advisorClients.length} sub="+3 this month" color="from-blue-500/20 to-blue-700/10" />
          <KPICard Icon={Briefcase} label="Total AUM" value={fmt(totalAUM)} sub="+8.4% MoM" color="from-emerald-500/20 to-emerald-700/10" />
          <KPICard Icon={TrendingUp} label="Active SIPs" value={totalSIPs} sub={fmt(totalSIPs * 12000) + "/mo flow"} color="from-indigo-500/20 to-indigo-700/10" />
          <KPICard Icon={IndianRupee} label="Feb Commission" value={fmt(commission)} sub="+8.6% vs Jan" color="from-amber-500/20 to-amber-700/10" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass-strong rounded-2xl p-6">
            <div className="flex justify-between mb-4">
              <h3 className="font-bold">Commission Trend</h3>
              <span className="text-xs text-slate-500">Last 6 months</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={commissionTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip
                  contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
                  formatter={(v) => fmt(v)}
                />
                <Line type="monotone" dataKey="value" stroke="#60A5FA" strokeWidth={3} dot={{ fill: "#60A5FA", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="glass-strong rounded-2xl p-6">
            <h3 className="font-bold mb-4">AUM by Segment</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={[
                { name: "Equity", v: 84 },
                { name: "Debt", v: 32 },
                { name: "Hybrid", v: 41 },
                { name: "Gold", v: 12 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="name" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
                  formatter={(v) => `₹${v} Cr`}
                />
                <Bar dataKey="v" fill="#60A5FA" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
            <div>
              <h3 className="font-bold text-lg">Client Management</h3>
              <p className="text-xs text-slate-500 mt-1">{filtered.length} of {advisorClients.length} clients shown</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or ID..."
                  className="bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm w-64 focus:border-blue-500 outline-none"
                  data-testid="client-search"
                />
              </div>
              <button className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="upload-doc">
                <FileUp className="w-4 h-4" /> Upload
              </button>
              <button className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="add-client">
                <Plus className="w-4 h-4" /> Add Client
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
                  <th className="py-3">Client ID</th>
                  <th className="py-3">Name</th>
                  <th className="py-3 text-right">AUM</th>
                  <th className="py-3 text-center">SIPs</th>
                  <th className="py-3 text-center">KYC</th>
                  <th className="py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <motion.tr
                    key={c.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-white/5 hover:bg-white/5"
                    data-testid={`client-row-${c.id}`}
                  >
                    <td className="py-4 font-mono text-blue-400">{c.id}</td>
                    <td className="py-4 font-semibold">{c.name}</td>
                    <td className="py-4 text-right font-mono">{fmt(c.aum)}</td>
                    <td className="py-4 text-center font-mono">{c.sips}</td>
                    <td className="py-4 text-center">
                      <KYCBadge status={c.kyc} />
                    </td>
                    <td className="py-4 text-slate-400">{c.joined}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <BellIcon className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold">Notifications</h3>
            </div>
            <div className="space-y-3">
              {[
                { t: "New SIP request from Anita Reddy — ₹5,000/m Bluechip", time: "2h ago" },
                { t: "KYC approved for Arjun Mehta (WL-1312)", time: "5h ago" },
                { t: "Quarterly commission credited — ₹2,91,000", time: "1d ago" },
                { t: "Mirae Asset announced bonus dividend — Mar 12", time: "2d ago" },
              ].map((n, i) => (
                <div key={i} className="flex justify-between gap-3 pb-3 border-b border-white/5 last:border-0">
                  <p className="text-sm text-slate-300">{n.t}</p>
                  <span className="text-xs text-slate-500 whitespace-nowrap">{n.time}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-strong rounded-2xl p-6">
            <h3 className="font-bold mb-4">Quick Reports</h3>
            <div className="grid grid-cols-2 gap-3">
              {["Client Summary", "SIP Book", "Commission Report", "AUM Statement", "Tax Statement", "Brokerage Slip"].map((r) => (
                <button key={r} className="glass rounded-xl p-4 text-left hover:bg-blue-600/10 hover:border-blue-500/30 transition-colors" data-testid={`report-${r.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="text-sm font-semibold">{r}</div>
                  <div className="text-xs text-slate-500 mt-1">Generate PDF</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({ Icon, label, value, sub, color }) {
  return (
    <div className={`relative glass-strong rounded-2xl p-5 overflow-hidden`}>
      <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br ${color} blur-2xl`} />
      <div className="relative">
        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-3">
          <Icon className="w-4 h-4 text-blue-400" />
        </div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-2xl font-black mt-1 font-mono">{value}</div>
        <div className="text-xs text-emerald-400 mt-1">{sub}</div>
      </div>
    </div>
  );
}

function KYCBadge({ status }) {
  const map = {
    Verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    Pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    "In Review": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold border ${map[status]}`}>
      {status.toUpperCase()}
    </span>
  );
}
